import {Injectable} from '@angular/core';
import {
  createClient,
  isAuthApiError,
  isAuthSessionMissingError,
  type Factor,
  type OAuthAuthorizationDetails,
  type OAuthGrant,
  type Session,
  type SupabaseClient,
  type User,
  type UserIdentity,
} from '@supabase/supabase-js';
import {environment} from '../../environments/environment';
import {resolveSupabaseAuthEnv, supabaseAuthDev, supabaseAuthProd} from '../supabase.env';

export interface PasskeyInfo {
  id: string;
  friendly_name?: string;
  created_at: string;
  last_used_at?: string;
}

/** Only same-origin relative paths (blocks open redirects). */
export function sanitizeInternalPath(path: string | null | undefined): string | null {
  if (!path) {
    return null;
  }
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('://') || trimmed.includes('\\')) {
    return null;
  }
  return trimmed;
}

/**
 * External return URL for cross-app SSO handoff (e.g. iot-admin / erp-tool after logout).
 * Allows http://localhost|127.0.0.1 and https://*.mangopower.com / *.mangopower.com.cn.
 */
export function sanitizeReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.username || url.password) {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (url.protocol === 'http:' && (host === 'localhost' || host === '127.0.0.1')) {
    return url.toString();
  }
  if (
    url.protocol === 'https:'
    && (
      host === 'mangopower.com'
      || host.endsWith('.mangopower.com')
      || host === 'mangopower.com.cn'
      || host.endsWith('.mangopower.com.cn')
    )
  ) {
    return url.toString();
  }
  return null;
}

const EXTERNAL_RETURN_KEY = 'mp-web-mp-account-external-return';

/** Persist allowlisted return URL across provider OAuth redirects. */
export function stashExternalReturnUrl(raw: string | null | undefined): void {
  const url = sanitizeReturnUrl(raw);
  try {
    if (url) {
      sessionStorage.setItem(EXTERNAL_RETURN_KEY, url);
    } else {
      sessionStorage.removeItem(EXTERNAL_RETURN_KEY);
    }
  } catch {
    // ignore
  }
}

export function takeExternalReturnUrl(): string | null {
  try {
    const value = sessionStorage.getItem(EXTERNAL_RETURN_KEY);
    sessionStorage.removeItem(EXTERNAL_RETURN_KEY);
    return sanitizeReturnUrl(value);
  } catch {
    return null;
  }
}

const ALLOWED_OAUTH_AUTHORIZE_HOSTS = new Set([
  new URL(supabaseAuthProd.url).hostname,
  new URL(supabaseAuthDev.url).hostname,
]);

/**
 * Supabase OAuth authorize URL handed from a client (e.g. iot-admin logout resume).
 * Host must be a known Auth project; redirect_uri must itself be allowlisted.
 */
export function sanitizeOAuthAuthorizeUrl(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') {
    return null;
  }
  if (!ALLOWED_OAUTH_AUTHORIZE_HOSTS.has(url.hostname.toLowerCase())) {
    return null;
  }
  if (url.pathname !== '/auth/v1/oauth/authorize') {
    return null;
  }
  if (url.searchParams.get('response_type') !== 'code') {
    return null;
  }
  if (!url.searchParams.get('client_id') || !url.searchParams.get('state') || !url.searchParams.get('code_challenge')) {
    return null;
  }
  if (!sanitizeReturnUrl(url.searchParams.get('redirect_uri'))) {
    return null;
  }
  return url.toString();
}

/** user_metadata key: true after password is set or password login succeeds. */
export const PASSWORD_SET_META_KEY = 'password_set';

/** Fresh OAuth/OTP accounts older than this are treated as legacy (do not backfill false). */
const NEW_USER_PASSWORD_MARK_WINDOW_MS = 10 * 60 * 1000;

export type OAuthIntent = 'login' | 'signup' | 'link-google' | 'link-apple';

/** Outcome of email/password sign-up (handles confirm-email vs duplicate quietly). */
export type SignUpResult =
  | {status: 'signed_in'; session: Session}
  | {status: 'confirm_email'; email: string}
  | {status: 'already_registered'; email: string};

function isAlreadyRegisteredAuthError(error: unknown): boolean {
  if (!isAuthApiError(error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    error.code === 'user_already_exists'
    || error.code === 'email_exists'
    || message.includes('already registered')
    || message.includes('already been registered')
    || message.includes('user already exists')
  );
}

const OAUTH_INTENT_KEY = 'mp-web-mp-account-oauth-intent';

const OAUTH_INTENTS = new Set<OAuthIntent>(['login', 'signup', 'link-google', 'link-apple']);

export function setOAuthIntent(intent: OAuthIntent): void {
  try {
    sessionStorage.setItem(OAUTH_INTENT_KEY, intent);
  } catch {
    // ignore quota / private-mode failures
  }
}

export function takeOAuthIntent(): OAuthIntent | null {
  try {
    const value = sessionStorage.getItem(OAUTH_INTENT_KEY);
    sessionStorage.removeItem(OAUTH_INTENT_KEY);
    return value && OAUTH_INTENTS.has(value as OAuthIntent) ? (value as OAuthIntent) : null;
  } catch {
    return null;
  }
}

/** OAuth PKCE callback (must be allow-listed in Supabase Auth redirect URLs). */
export function buildAuthCallbackUrl(origin = window.location.origin, next?: string): string {
  const url = new URL('/auth/callback', origin);
  const safeNext = sanitizeInternalPath(next);
  if (safeNext) {
    url.searchParams.set('next', safeNext);
  }
  return url.toString();
}

const TERMINAL_REFRESH_ERROR_CODES = new Set([
  'refresh_token_already_used',
  'refresh_token_not_found',
  'session_expired',
  'session_not_found',
  'user_banned',
  'user_not_found',
]);

export function isTerminalRefreshError(error: unknown): boolean {
  return isAuthSessionMissingError(error)
    || (isAuthApiError(error) && Boolean(error.code && TERMINAL_REFRESH_ERROR_CODES.has(error.code)));
}

@Injectable({
  providedIn: 'root',
})
export class SupabaseAuthService {
  private client: SupabaseClient | null = null;

  private getClient(): SupabaseClient {
    if (!this.client) {
      const env = resolveSupabaseAuthEnv(environment.production);
      this.client = createClient(env.url, env.publishableKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false,
          flowType: 'pkce',
          storageKey: env.storageKey,
          experimental: {passkey: true},
        },
      });
    }
    return this.client;
  }

  async signInWithPassword(email: string, password: string): Promise<Session> {
    const {data, error} = await this.getClient().auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error || !data.session) {
      throw error ?? new Error('Supabase sign-in returned no session.');
    }
    return data.session;
  }

  async signUpWithPassword(email: string, password: string): Promise<SignUpResult> {
    const normalizedEmail = email.trim().toLowerCase();
    const {data, error} = await this.getClient().auth.signUp({
      email: normalizedEmail,
      password,
    });
    if (error) {
      if (isAlreadyRegisteredAuthError(error)) {
        return {status: 'already_registered', email: normalizedEmail};
      }
      throw error;
    }
    if (data.session) {
      return {status: 'signed_in', session: data.session};
    }
    // Supabase returns an obfuscated user with empty identities for a repeated signup
    // when email confirmations are enabled (anti-enumeration). Treat that as already registered.
    const identities = data.user?.identities ?? [];
    if (!data.user || identities.length === 0) {
      return {status: 'already_registered', email: normalizedEmail};
    }
    return {status: 'confirm_email', email: normalizedEmail};
  }

  async resendSignupConfirmation(email: string): Promise<void> {
    const {error} = await this.getClient().auth.resend({
      type: 'signup',
      email: email.trim().toLowerCase(),
    });
    if (error) {
      throw error;
    }
  }

  /** Complete email/password sign-up after the user enters the email OTP from the confirmation mail. */
  async verifySignupOtp(email: string, token: string): Promise<Session> {
    const {data, error} = await this.getClient().auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: token.trim(),
      type: 'signup',
    });
    if (error || !data.session) {
      throw error ?? new Error('Supabase signup OTP verification returned no session.');
    }
    return data.session;
  }

  /** Complete password recovery after the user enters the 6-digit email OTP. */
  async verifyRecoveryOtp(email: string, token: string): Promise<Session> {
    const {data, error} = await this.getClient().auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: token.trim(),
      type: 'recovery',
    });
    if (error || !data.session) {
      throw error ?? new Error('Supabase recovery OTP verification returned no session.');
    }
    return data.session;
  }

  async resetPasswordForEmail(email: string): Promise<void> {
    const {error} = await this.getClient().auth.resetPasswordForEmail(email.trim().toLowerCase());
    if (error) {
      throw error;
    }
  }

  /**
   * Update password for the currently signed-in user.
   * Pass `nonce` from the email OTP sent by {@link reauthenticate}.
   */
  async updatePassword(password: string, nonce?: string): Promise<void> {
    const payload: {password: string; nonce?: string; data?: Record<string, unknown>} = {password};
    if (nonce) {
      payload.nonce = nonce;
      payload.data = {[PASSWORD_SET_META_KEY]: true};
    }
    const {error} = await this.getClient().auth.updateUser(payload);
    if (error) {
      throw error;
    }
  }

  /**
   * Send a reauthentication OTP (nonce) to the signed-in user's email/phone.
   * Requires Secure password change in Supabase Auth email provider settings.
   */
  async reauthenticate(): Promise<void> {
    const {error} = await this.getClient().auth.reauthenticate();
    if (error) {
      throw error;
    }
  }

  async getUser(): Promise<User> {
    const {data, error} = await this.getClient().auth.getUser();
    if (error || !data.user) {
      throw error ?? new Error('Failed to load user.');
    }
    return data.user;
  }

  /**
   * Prompt only when metadata is explicitly `false` (OTP / third-party sign-up).
   * Legacy users with missing key are NOT treated as needing setup.
   */
  async needsPasswordSetup(): Promise<boolean> {
    return (await this.getPasswordSetMeta()) === false;
  }

  async hasPasswordSet(): Promise<boolean> {
    const meta = await this.getPasswordSetMeta();
    return meta === true;
  }

  async markPasswordSet(): Promise<void> {
    if (await this.hasPasswordSet()) {
      return;
    }
    const {error} = await this.getClient().auth.updateUser({
      data: {[PASSWORD_SET_META_KEY]: true},
    });
    if (error) {
      throw error;
    }
  }

  async markPasswordUnsetIfNeeded(): Promise<void> {
    if (await this.hasPasswordSet()) {
      return;
    }
    const {error} = await this.getClient().auth.updateUser({
      data: {[PASSWORD_SET_META_KEY]: false},
    });
    if (error) {
      throw error;
    }
  }

  async markPasswordUnsetForNewUserIfNeeded(
    maxAgeMs = NEW_USER_PASSWORD_MARK_WINDOW_MS,
  ): Promise<void> {
    if (await this.hasPasswordSet()) {
      return;
    }
    if ((await this.getPasswordSetMeta()) === false) {
      return;
    }

    const user = await this.getUser();
    if (!user.created_at) {
      return;
    }

    const createdAt = Date.parse(user.created_at);
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > maxAgeMs) {
      return;
    }

    const {error} = await this.getClient().auth.updateUser({
      data: {[PASSWORD_SET_META_KEY]: false},
    });
    if (error) {
      throw error;
    }
  }

  private async getPasswordSetMeta(): Promise<boolean | undefined> {
    const user = await this.getUser();
    const value = user.user_metadata?.[PASSWORD_SET_META_KEY];
    if (value === true) {
      return true;
    }
    if (value === false) {
      return false;
    }
    return undefined;
  }

  /** Supabase standard user_metadata.full_name (also mirrors `name` for OAuth parity). */
  async updateFullName(fullName: string): Promise<User> {
    const value = fullName.trim();
    const {data, error} = await this.getClient().auth.updateUser({
      data: {
        full_name: value,
        name: value,
      },
    });
    if (error || !data.user) {
      throw error ?? new Error('Failed to update full name.');
    }
    return data.user;
  }

  /** @deprecated Prefer updateFullName — kept for any leftover call sites. */
  async updateNickname(nickname: string): Promise<User> {
    return this.updateFullName(nickname);
  }

  async exchangeCodeForSession(code: string): Promise<Session> {
    const {data, error} = await this.getClient().auth.exchangeCodeForSession(code);
    if (error || !data.session) {
      throw error ?? new Error('Supabase auth callback returned no session.');
    }
    return data.session;
  }

  async getSession(): Promise<Session | null> {
    const {data, error} = await this.getClient().auth.getSession();
    if (error) {
      throw error;
    }
    return data.session;
  }

  async getAccessToken(): Promise<string | null> {
    const session = await this.getSession();
    return session?.access_token ?? null;
  }

  /** Linked identities (Google / Apple / …) — used for avatar_url / picture. */
  async getUserIdentities(): Promise<UserIdentity[]> {
    const {data, error} = await this.getClient().auth.getUserIdentities();
    if (error) {
      throw error;
    }
    return data?.identities ?? [];
  }

  // ── Passkey ───────────────────────────────────────────────

  async signInWithPasskey(): Promise<Session> {
    const {data, error} = await this.getClient().auth.signInWithPasskey();
    if (error) {
      throw error;
    }
    if (!data.session) {
      throw new Error('Passkey sign-in returned no session.');
    }
    return data.session;
  }

  /**
   * Start Google sign-in (OAuth redirect).
   * Redirect URI must be allow-listed in Supabase Auth (same as OAuth callback).
   */
  /**
   * Start Google identity linking (OAuth redirect).
   * Requires Supabase: Google provider enabled + Manual Linking enabled + redirect URL allow-listed.
   */
  async linkGoogleIdentity(redirectTo = buildAuthCallbackUrl()): Promise<void> {
    setOAuthIntent('link-google');
    const {error} = await this.getClient().auth.linkIdentity({
      provider: 'google',
      options: {redirectTo},
    });
    if (error) {
      throw error;
    }
  }

  /**
   * Start Apple identity linking (OAuth redirect).
   * Requires Supabase: Apple provider enabled + Manual Linking enabled + redirect URL allow-listed.
   */
  async linkAppleIdentity(redirectTo = buildAuthCallbackUrl()): Promise<void> {
    setOAuthIntent('link-apple');
    const {error} = await this.getClient().auth.linkIdentity({
      provider: 'apple',
      options: {redirectTo},
    });
    if (error) {
      throw error;
    }
  }

  async unlinkIdentity(identity: UserIdentity): Promise<void> {
    const {error} = await this.getClient().auth.unlinkIdentity(identity);
    if (error) {
      throw error;
    }
  }

  async signInWithGoogle(redirectTo = buildAuthCallbackUrl()): Promise<void> {
    setOAuthIntent('login');
    const {error} = await this.getClient().auth.signInWithOAuth({
      provider: 'google',
      options: {redirectTo},
    });
    if (error) {
      throw error;
    }
  }

  /**
   * Start Apple sign-in (OAuth redirect).
   * Redirect URI must be allow-listed in Supabase Auth (same as OAuth callback).
   */
  async signInWithApple(redirectTo = buildAuthCallbackUrl()): Promise<void> {
    setOAuthIntent('login');
    const {error} = await this.getClient().auth.signInWithOAuth({
      provider: 'apple',
      options: {redirectTo},
    });
    if (error) {
      throw error;
    }
  }

  async registerPasskey(): Promise<PasskeyInfo> {
    const {data, error} = await this.getClient().auth.registerPasskey();
    if (error) {
      throw error;
    }
    if (!data) {
      throw new Error('Passkey registration returned no data.');
    }
    return data;
  }

  async listPasskeys(): Promise<PasskeyInfo[]> {
    const {data, error} = await this.getClient().auth.passkey.list();
    if (error) {
      throw error;
    }
    return data ?? [];
  }

  async renamePasskey(passkeyId: string, friendlyName: string): Promise<PasskeyInfo> {
    const {data, error} = await this.getClient().auth.passkey.update({passkeyId, friendlyName});
    if (error) {
      throw error;
    }
    return data!;
  }

  async deletePasskey(passkeyId: string): Promise<void> {
    const {error} = await this.getClient().auth.passkey.delete({passkeyId});
    if (error) {
      throw error;
    }
  }

  // ── MFA (TOTP) ────────────────────────────────────────────

  async listTotpFactors(): Promise<Factor<'totp', 'verified'>[]> {
    const {data, error} = await this.getClient().auth.mfa.listFactors();
    if (error) {
      throw error;
    }
    return data?.totp ?? [];
  }

  /** Incomplete enrollments leave unverified factors that block re-enroll (same name / slot). */
  async listUnverifiedTotpFactors(): Promise<Factor<'totp', 'unverified'>[]> {
    const {data, error} = await this.getClient().auth.mfa.listFactors();
    if (error) {
      throw error;
    }
    return (data?.all ?? []).filter(
      (factor): factor is Factor<'totp', 'unverified'> =>
        factor.factor_type === 'totp' && factor.status === 'unverified',
    );
  }

  async cleanupUnverifiedTotpFactors(): Promise<void> {
    const leftovers = await this.listUnverifiedTotpFactors();
    for (const factor of leftovers) {
      try {
        await this.unenrollFactor(factor.id);
      } catch {
        // Best-effort; enroll will surface a hard error if cleanup truly failed.
      }
    }
  }

  async enrollTotp(friendlyName = 'Authenticator'): Promise<{factorId: string; qrCode: string; secret: string}> {
    // Drop abandoned enrollments so friendly-name / factor-slot conflicts don't stick.
    await this.cleanupUnverifiedTotpFactors();

    const {data, error} = await this.getClient().auth.mfa.enroll({
      factorType: 'totp',
      friendlyName,
      issuer: 'Mango Power',
    });
    if (error || !data) {
      throw error ?? new Error('MFA enroll failed.');
    }
    return {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
    };
  }

  async challengeAndVerifyTotp(factorId: string, code: string): Promise<Session> {
    const {error} = await this.getClient().auth.mfa.challengeAndVerify({
      factorId,
      code: code.trim(),
    });
    if (error) {
      throw error;
    }
    const session = await this.getSession();
    if (!session) {
      throw new Error('MFA verify returned no session.');
    }
    return session;
  }

  async unenrollFactor(factorId: string): Promise<void> {
    const {error} = await this.getClient().auth.mfa.unenroll({factorId});
    if (error) {
      throw error;
    }
  }

  // ── OAuth 2.1 Server consent (this app acts as the IdP) ────

  /**
   * Fetch a pending authorization request's details for the consent screen.
   * Returns `{redirect_url}` instead when the user already consented — caller
   * should navigate there directly rather than showing a consent prompt.
   */
  async getOAuthAuthorizationDetails(
    authorizationId: string,
  ): Promise<OAuthAuthorizationDetails | {redirect_url: string}> {
    const {data, error} = await this.getClient().auth.oauth.getAuthorizationDetails(authorizationId);
    if (error || !data) {
      throw error ?? new Error('Failed to load authorization request.');
    }
    return data;
  }

  async approveOAuthAuthorization(authorizationId: string): Promise<string> {
    const {data, error} = await this.getClient().auth.oauth.approveAuthorization(authorizationId, {
      skipBrowserRedirect: true,
    });
    if (error || !data) {
      throw error ?? new Error('Failed to approve authorization request.');
    }
    return data.redirect_url;
  }

  async denyOAuthAuthorization(authorizationId: string): Promise<string> {
    const {data, error} = await this.getClient().auth.oauth.denyAuthorization(authorizationId, {
      skipBrowserRedirect: true,
    });
    if (error || !data) {
      throw error ?? new Error('Failed to deny authorization request.');
    }
    return data.redirect_url;
  }

  /** Apps the user has authorized via the OAuth 2.1 Server (e.g. iot-admin). */
  async listOAuthGrants(): Promise<OAuthGrant[]> {
    const {data, error} = await this.getClient().auth.oauth.listGrants();
    if (error) {
      throw error;
    }
    return data ?? [];
  }

  /** Revokes an app's access — ends its active sessions and invalidates its refresh tokens. */
  async revokeOAuthGrant(clientId: string): Promise<void> {
    const {error} = await this.getClient().auth.oauth.revokeGrant({clientId});
    if (error) {
      throw error;
    }
  }

  // ── Sessions ──────────────────────────────────────────────

  async signOutCurrentSession(): Promise<void> {
    const {error} = await this.getClient().auth.signOut({scope: 'local'});
    if (error) {
      throw error;
    }
  }

  async signOutOtherSessions(): Promise<void> {
    const {error} = await this.getClient().auth.signOut({scope: 'others'});
    if (error) {
      throw error;
    }
  }

  async signOutAllSessions(): Promise<void> {
    const {error} = await this.getClient().auth.signOut({scope: 'global'});
    if (error) {
      throw error;
    }
  }
}
